import { useState, useEffect } from 'react'
import { APIResponse, SchoolListing } from '../types/api'
import { getPublisherParams } from '../utils/publisherParams'
import {
  applyUrlPassthrough,
  pruneEmptyParams,
} from '../utils/urlPassthrough'
import { getWebLandingURL } from '../utils/landingUrl'

interface TrackingParams {
  sub1?: string
  sub2?: string
  sub3?: string
}

interface SearchParams {
  degreeInterest: number
  mProgram: number
  zipCode?: string
  tracking?: TrackingParams
}

// Default tracking values used when no URL params are provided
const DEFAULT_TRACKING: Required<TrackingParams> = {
  sub1: '{campaignid}_{targetid}_main',
  sub2: '{matchtype}_{adgroupid}_{device}',
  sub3: '{gclid}',
}

const M_PUB_GROUP_ID = 'PCGP5964228910'

export function useSchoolSearch({
  degreeInterest,
  mProgram,
  zipCode,
  tracking,
}: SearchParams) {
  const [schools, setSchools] = useState<SchoolListing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Merge provided tracking params with defaults
  const sub1 = tracking?.sub1 || DEFAULT_TRACKING.sub1
  const sub2 = tracking?.sub2 || DEFAULT_TRACKING.sub2
  const sub3 = tracking?.sub3 || DEFAULT_TRACKING.sub3

  // Publisher identification: URL params (mPubGroupID, or mPubID +
  // mPubCampaignID) override the default group id when present.
  const pubSearch = typeof window !== 'undefined' ? window.location.search : ''

  useEffect(() => {
    const fetchSchools = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({
          degreeInterest: String(degreeInterest),
          mProgram: String(mProgram),
          highestEducation: '1',
          gradYear: '2008',
          campusType: '1',
          militaryAffiliation: '1',
          startTimeframe: '1',
          sub1,
          sub2,
          sub3,
          zipCode: zipCode || '33303',
          mediaType: '1',
          redirect: '0',
          webLandingURL: getWebLandingURL(),
        })
        // Forward every other param on the landing URL (utm_*, clickid, …).
        applyUrlPassthrough(params, pubSearch)
        // mPubGroupID OR (mPubID + mPubCampaignID), per URL params.
        const pub = getPublisherParams(pubSearch, {
          mPubGroupID: M_PUB_GROUP_ID,
        })
        Object.entries(pub).forEach(([k, v]) => params.set(k, v))
        const url = `https://api.media-matchers.com/search?${pruneEmptyParams(params).toString()}`
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error('Failed to fetch schools')
        }
        const data: APIResponse = await response.json()
        const sortedSchools = (data.result || []).sort(
          (a, b) => a.mPosition - b.mPosition,
        )
        setSchools(sortedSchools)
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching schools',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchSchools()
  }, [degreeInterest, mProgram, zipCode, sub1, sub2, sub3, pubSearch])

  return { schools, loading, error }
}
